<template>
  <Teleport to="body">
    <WizardModalModern
      v-if="isVisible"
      :show-modal="isVisible"
      :modal-title="t('Adicionar Produtos')"
      :steps="wizardSteps"
      :current-step="0"
      wizard-icon="pi pi-box"
      :wizard-subtitle="t('Selecione os produtos e quantidades')"
      elem-id="pdv-produtos-wizard"
      :validate-fn="() => currentTotal > 0"
      @close="handleClose"
      @cancel="handleClose"
      @finish="handleFinish"
    >
      <template #step-0>
        <h3 class="step-title">{{ t('Produtos Disponíveis') }}</h3>
        <p class="step-desc">{{ t('Selecione os produtos e defina as quantidades') }}</p>
        <div class="products-step-wrapper">
          <SOProductSelector
            v-model="selectedProducts"
            :show-total="true"
            :franchiseId="franchiseId"
            inline
            @total-change="handleTotalChange"
          />
        </div>
      </template>
    </WizardModalModern>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModalModern from '../../../ui/WizardModalModern.vue';
import { eventBus } from '../../../eventBus';
import { useSOCheckout } from '../../../composables/useSOCheckout';
import { log, error as logError } from '../../../utils/DebugLogger';

type Id = number | string;

interface OpenPayload {
  orderId: Id;
  franchiseId?: Id | null;
  [key: string]: unknown;
}

const { t } = useI18n();
const { addItem } = useSOCheckout();

const isVisible = ref<boolean>(false);
const orderId = ref<Id | null>(null);
const franchiseId = ref<Id | null>(null);
const selectedProducts = ref<Record<string, number>>({});
const currentTotal = ref<number>(0);

const wizardSteps = computed(() => [
  { title: t('Produtos'), key: 'products' }
]);

const handleTotalChange = (total: number): void => {
  currentTotal.value = total;
};

const handleClose = (): void => {
  isVisible.value = false;
  selectedProducts.value = {};
  currentTotal.value = 0;
};

const handleFinish = async (): Promise<void> => {
  log('PDVProdutosWizardModal', 'handleFinish', 'Adding products to order', orderId.value);
  try {
    for (const [prodId, qty] of Object.entries(selectedProducts.value)) {
      if (qty > 0 && orderId.value) {
        await addItem(orderId.value, {
          type: 'product',
          product_id: parseInt(String(prodId)),
          quantity: qty
        });
      }
    }
    eventBus.emit('globalMessage.success', t('Produtos adicionados à ordem'));
    eventBus.emit('pdv.item-added');
    handleClose();
  } catch (err) {
    // The error toast is emitted by useSOCheckout.addItem itself.
    logError('PDVProdutosWizardModal', 'handleFinish', 'Error', err);
  }
};

const handleModalOpen = (data?: OpenPayload): void => {
  log('PDVProdutosWizardModal', 'handleModalOpen', 'Opening', data);
  if (!data) return;
  orderId.value = data.orderId;
  franchiseId.value = data.franchiseId ?? null;
  isVisible.value = true;
  selectedProducts.value = {};
  currentTotal.value = 0;
};

onMounted(() => {
  eventBus.on('modal.pdv-produtos-wizard.open', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-produtos-wizard.open', handleModalOpen);
});
</script>

<style scoped>
.step-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--app-text-color);
  margin: 0 0 4px 0;
}

.step-desc {
  font-size: 0.85rem;
  color: var(--app-text-secondary-color);
  margin: 0 0 16px 0;
}

/* ── Products Step Wrapper ── */
.products-step-wrapper :deep(.so-product-selector) {
  gap: 12px;
}

.products-step-wrapper :deep(.so-product-selector > button) {
  border-radius: 8px;
  align-self: flex-start;
}

.products-step-wrapper :deep(.selected-products) {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.products-step-wrapper :deep(.selected-product-row) {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--p-surface-0);
  border: 1.5px solid var(--p-surface-200);
  border-radius: 8px;
  transition: all 150ms ease;
}

.products-step-wrapper :deep(.selected-product-row:hover) {
  border-color: var(--p-surface-300);
}

.products-step-wrapper :deep(.selected-product-row .product-name) {
  flex: 1;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--app-text-color);
}

.products-step-wrapper :deep(.selected-product-row .product-price) {
  color: var(--app-text-secondary-color);
  min-width: 70px;
  text-align: right;
  font-size: 0.85rem;
}

.products-step-wrapper :deep(.selected-product-row .product-subtotal) {
  font-weight: 700;
  min-width: 90px;
  text-align: right;
  color: var(--app-primary-color);
}

.products-step-wrapper :deep(.qty-controls) {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--p-surface-50);
  border-radius: 8px;
  padding: 2px 4px;
}

.products-step-wrapper :deep(.qty-controls .qty-value) {
  min-width: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 0.85rem;
}

.products-step-wrapper :deep(.products-total) {
  text-align: right;
  font-weight: 700;
  font-size: 0.95rem;
  padding-top: 12px;
  margin-top: 8px;
  border-top: 2px solid var(--p-surface-200);
  color: var(--app-text-color);
}

.products-step-wrapper :deep(.no-products) {
  text-align: center;
  padding: 32px 20px;
  color: var(--p-text-muted-color);
  background: var(--p-surface-50);
  border-radius: 8px;
  border: 1.5px dashed var(--p-surface-200);
  font-size: 0.875rem;
}
</style>
